import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Placeholder } from '@tiptap/extensions';
import { Editor, EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  ImagePlus,
  Italic,
  Link2,
  Link2Off,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo2,
  Strikethrough,
  Underline as UnderlineIcon,
  Undo2
} from 'lucide-react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Input } from './input';
import { LessonImage } from './lesson-image-extension';

/** Uploads one file, returning the stable path (persisted) and a signed url (shown right now). */
export type ImageUploadHandler = (file: File) => Promise<{ path: string; url: string }>;

export interface RichTextEditorProps {
  /** HTML string. Kept in sync when changed from the outside (e.g. after loading a lesson). */
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** When set, an image button appears in the toolbar. Without it images cannot be inserted. */
  onImageUpload?: ImageUploadHandler;
}

// forwardRef so it can serve as a Radix popover trigger — Radix needs the node to anchor against.
const ToolbarButton = forwardRef<
  HTMLButtonElement,
  {
    onClick?: () => void;
    isActive?: boolean;
    disabled?: boolean;
    label: string;
    children: React.ReactNode;
  }
>(({ onClick, isActive, disabled, label, children, ...props }, ref) => (
  <button
    {...props}
    ref={ref}
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    aria-pressed={isActive}
    title={label}
    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
      isActive ? 'bg-[#12282A] text-white' : 'text-[#12282A] hover:bg-[#F0F4FC]'
    }`}>
    {children}
  </button>
));

const ToolbarDivider = () => <span className="w-px h-5 bg-[#E4E7EC] mx-1" />;

/**
 * Accepts input without a scheme — people paste `7i7.de` far more often than `https://7i7.de`, and
 * a link stored without one would resolve relative to the app and quietly 404.
 */
const normalizeUrl = (input: string): string | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const withScheme = /^(https?:\/\/|mailto:)/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(withScheme);
    // A bare word like "test" parses as https://test — reject it, there is no dot to make it a host.
    if (parsed.protocol !== 'mailto:' && !parsed.hostname.includes('.')) return null;
    return parsed.toString();
  } catch {
    return null;
  }
};

const LinkPopover = ({ editor, disabled, isActive }: { editor: Editor; disabled?: boolean; isActive?: boolean }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [container, setContainer] = useState<HTMLElement | null>(null);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) return;
    /**
     * A modal Radix dialog puts `pointer-events: none` on everything outside its content and traps
     * focus there. Portalling to the body would drop the panel outside that boundary — unclickable
     * and unfillable. Resolved per open, since the editor is also used outside any dialog.
     */
    setContainer(triggerRef.current?.closest<HTMLElement>('[role="dialog"]') ?? null);
    // Prefill from the link under the caret so opening it on an existing link edits instead of replaces.
    setUrl((editor.getAttributes('link').href as string | undefined) ?? '');
    setError(null);
  };

  const applyLink = () => {
    const normalized = normalizeUrl(url);
    if (!normalized) {
      setError('Bitte eine gültige URL angeben.');
      return;
    }
    // extendMarkRange so editing an existing link updates the whole link, not just the selected part.
    editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run();
    setIsOpen(false);
  };

  return (
    // The shared ./popover paints a full-screen scrim behind its content — right for a modal, wrong
    // for a link field inside a dialog. Using the primitive directly keeps the page undimmed.
    <PopoverPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Trigger asChild>
        <ToolbarButton ref={triggerRef} label="Link setzen" disabled={disabled} isActive={isActive}>
          <Link2 className="w-4 h-4" />
        </ToolbarButton>
      </PopoverPrimitive.Trigger>
      {/* Portalled so the editor's overflow-hidden container cannot clip the panel. */}
      <PopoverPrimitive.Portal container={container}>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          // Above the dialog it opens from, which sits at z-50.
          className="z-[60] w-[320px] p-3 flex flex-col gap-2 bg-white rounded-xl border border-[#E4E7EC] shadow-lg outline-none"
          // Radix would hand focus back to the trigger on close, pulling the caret out of the editor.
          onCloseAutoFocus={(event) => event.preventDefault()}>
          <label className="text-[13px] font-bold text-[#12282A]">Link-URL</label>
          <Input
            value={url}
            autoFocus
            placeholder="7i7.de"
            onChange={(e) => {
              setUrl(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              // The editor sits inside a form — Enter here must not submit the lesson.
              if (e.key === 'Enter') {
                e.preventDefault();
                applyLink();
              }
            }}
          />
          {error && <p className="text-[12px] text-red-500">{error}</p>}
          {/* Removing a link lives on the dedicated toolbar button next to the trigger, not in here. */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={applyLink}
              className="text-[13px] px-3 py-1.5 rounded-lg bg-[#12282A] text-white hover:opacity-90 transition-opacity">
              Setzen
            </button>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};

/** Toolbar image button: opens a file picker, uploads, then inserts an img carrying the stored path. */
const ImageButton = ({ editor, disabled, onImageUpload }: { editor: Editor; disabled?: boolean; onImageUpload: ImageUploadHandler }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setIsUploading(true);
    try {
      const { path, url } = await onImageUpload(file);
      // src shows the signed url now; data-path is what gets persisted (see serializeLessonHtml).
      editor
        .chain()
        .focus()
        .setImage({ src: url, alt: file.name })
        .updateAttributes('image', { 'data-path': path })
        .run();
    } finally {
      setIsUploading(false);
      // Reset so picking the same file twice fires change again.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <ToolbarButton label="Bild einfügen" disabled={disabled || isUploading} onClick={() => inputRef.current?.click()}>
        <ImagePlus className="w-4 h-4" />
      </ToolbarButton>
    </>
  );
};

const Toolbar = ({ editor, disabled, onImageUpload }: { editor: Editor; disabled?: boolean; onImageUpload?: ImageUploadHandler }) => {
  /**
   * Moving the caret changes the editor state without re-rendering React, so reading `isActive`
   * during render would leave the buttons showing the state from the last keystroke. This subscribes
   * to the editor and only re-renders when one of these values actually flips.
   */
  const state = useEditorState({
    editor,
    selector: ({ editor: instance }) => ({
      isBold: instance.isActive('bold'),
      isItalic: instance.isActive('italic'),
      isUnderline: instance.isActive('underline'),
      isStrike: instance.isActive('strike'),
      isH1: instance.isActive('heading', { level: 1 }),
      isH2: instance.isActive('heading', { level: 2 }),
      isH3: instance.isActive('heading', { level: 3 }),
      isBulletList: instance.isActive('bulletList'),
      isOrderedList: instance.isActive('orderedList'),
      isBlockquote: instance.isActive('blockquote'),
      isCode: instance.isActive('code'),
      isLink: instance.isActive('link'),
      canUndo: instance.can().undo(),
      canRedo: instance.can().redo()
    })
  });

  return (
    <div className="flex items-center flex-wrap gap-0.5 px-2 py-1.5 border-b border-[#E4E7EC] bg-white rounded-t-xl shrink-0">
      <ToolbarButton label="Fett" disabled={disabled} isActive={state.isBold} onClick={() => editor.chain().focus().toggleBold().run()}>
        <Bold className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton label="Kursiv" disabled={disabled} isActive={state.isItalic} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <Italic className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Unterstrichen"
        disabled={disabled}
        isActive={state.isUnderline}
        onClick={() => editor.chain().focus().toggleUnderline().run()}>
        <UnderlineIcon className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Durchgestrichen"
        disabled={disabled}
        isActive={state.isStrike}
        onClick={() => editor.chain().focus().toggleStrike().run()}>
        <Strikethrough className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarDivider />

      <ToolbarButton
        label="Überschrift 1"
        disabled={disabled}
        isActive={state.isH1}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
        <Heading1 className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Überschrift 2"
        disabled={disabled}
        isActive={state.isH2}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
        <Heading2 className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Überschrift 3"
        disabled={disabled}
        isActive={state.isH3}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
        <Heading3 className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarDivider />

      <ToolbarButton
        label="Aufzählung"
        disabled={disabled}
        isActive={state.isBulletList}
        onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <List className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Nummerierte Liste"
        disabled={disabled}
        isActive={state.isOrderedList}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        <ListOrdered className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton label="Zitat" disabled={disabled} isActive={state.isBlockquote} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
        <Quote className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton label="Code" disabled={disabled} isActive={state.isCode} onClick={() => editor.chain().focus().toggleCode().run()}>
        <Code className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton label="Trennlinie" disabled={disabled} onClick={() => editor.chain().focus().setHorizontalRule().run()}>
        <Minus className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarDivider />

      <LinkPopover editor={editor} disabled={disabled} isActive={state.isLink} />
      <ToolbarButton label="Link entfernen" disabled={disabled || !state.isLink} onClick={() => editor.chain().focus().unsetLink().run()}>
        <Link2Off className="w-4 h-4" />
      </ToolbarButton>

      {onImageUpload && (
        <>
          <ToolbarDivider />
          <ImageButton editor={editor} disabled={disabled} onImageUpload={onImageUpload} />
        </>
      )}

      <ToolbarDivider />

      <ToolbarButton label="Rückgängig" disabled={disabled || !state.canUndo} onClick={() => editor.chain().focus().undo().run()}>
        <Undo2 className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton label="Wiederherstellen" disabled={disabled || !state.canRedo} onClick={() => editor.chain().focus().redo().run()}>
        <Redo2 className="w-4 h-4" />
      </ToolbarButton>
    </div>
  );
};

const RichTextEditor = ({ value, onChange, placeholder, disabled, onImageUpload }: RichTextEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: { openOnClick: false, autolink: true },
        heading: { levels: [1, 2, 3] }
      }),
      LessonImage,
      Placeholder.configure({ placeholder: placeholder ?? '' })
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: instance }) => onChange(instance.getHTML())
  });

  // Sync content coming from the outside (e.g. a lesson loaded after mount) without echoing our own edits.
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() === value) return;
    editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) return null;

  return (
    // `resize-y` needs a non-visible overflow to work. min/max bound how far it can be dragged.
    <div className="border border-[#E4E7EC] rounded-xl bg-white overflow-hidden focus-within:border-[#5FD5DE] transition-colors flex flex-col h-[240px] min-h-[140px] max-h-[70vh] resize-y">
      <Toolbar editor={editor} disabled={disabled} onImageUpload={onImageUpload} />
      {/* Flex chain down to .ProseMirror (see index.css) so the whole area is clickable, not just line one. */}
      <EditorContent editor={editor} className="rich-text-content flex flex-col flex-1 overflow-y-auto px-3 py-2 text-[14px] text-[#12282A]" />
    </div>
  );
};

export default RichTextEditor;
